import type { GameState } from './game';

/** Number of manual save slots available. */
export const MANUAL_SLOT_COUNT = 3;

/** Key prefix for localStorage entries. */
const STORAGE_PREFIX = 'kan-vn-save';

/** Key for the autosave slot. */
const AUTOSAVE_KEY = `${STORAGE_PREFIX}-auto`;

/** Key for a manual save slot (1-indexed). */
function slotKey(slot: number): string {
  if (slot < 1 || slot > MANUAL_SLOT_COUNT) {
    throw new Error(`Invalid slot number: ${slot}. Must be 1–${MANUAL_SLOT_COUNT}.`);
  }
  return `${STORAGE_PREFIX}-slot-${slot}`;
}

/**
 * Metadata stored alongside each save for display purposes.
 */
export interface SaveMeta {
  /** ISO-8601 timestamp of when the save was created. */
  timestamp: string;
  /** The script title at time of save. */
  scriptTitle: string;
  /** The beat id at time of save. */
  beatId: string;
}

/**
 * Full save entry persisted to localStorage.
 */
export interface SaveEntry {
  meta: SaveMeta;
  state: GameState;
}

// ── Autosave ──

/** Write an autosave. */
export function autosave(state: GameState, scriptTitle: string): void {
  const entry: SaveEntry = {
    meta: {
      timestamp: new Date().toISOString(),
      scriptTitle,
      beatId: state.beatId,
    },
    state,
  };
  localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(entry));
}

/** Load the autosave, or null if none exists. */
export function loadAutosave(): SaveEntry | null {
  return readEntry(AUTOSAVE_KEY);
}

/** Check whether an autosave exists. */
export function hasAutosave(): boolean {
  return localStorage.getItem(AUTOSAVE_KEY) !== null;
}

// ── Manual slots ──

/** Save to a manual slot (1–3). */
export function saveToSlot(slot: number, state: GameState, scriptTitle: string): void {
  const entry: SaveEntry = {
    meta: {
      timestamp: new Date().toISOString(),
      scriptTitle,
      beatId: state.beatId,
    },
    state,
  };
  localStorage.setItem(slotKey(slot), JSON.stringify(entry));
}

/** Load from a manual slot (1–3), or null if empty. */
export function loadFromSlot(slot: number): SaveEntry | null {
  return readEntry(slotKey(slot));
}

/** Delete a manual slot (1–3). */
export function deleteSlot(slot: number): void {
  localStorage.removeItem(slotKey(slot));
}

/** Return metadata for all slots (1–3). Null entries mean the slot is empty. */
export function listSlots(): (SaveEntry | null)[] {
  const result: (SaveEntry | null)[] = [];
  for (let i = 1; i <= MANUAL_SLOT_COUNT; i++) {
    result.push(loadFromSlot(i));
  }
  return result;
}

// ── Helpers ──

function readEntry(key: string): SaveEntry | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isValidSaveEntry(parsed)) {
      return parsed;
    }
    // Corrupted data — remove it
    localStorage.removeItem(key);
    return null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function isValidSaveEntry(value: unknown): value is SaveEntry {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  if (typeof obj['meta'] !== 'object' || obj['meta'] === null) return false;
  if (typeof obj['state'] !== 'object' || obj['state'] === null) return false;

  const meta = obj['meta'] as Record<string, unknown>;
  if (typeof meta['timestamp'] !== 'string') return false;
  if (typeof meta['scriptTitle'] !== 'string') return false;
  if (typeof meta['beatId'] !== 'string') return false;

  const state = obj['state'] as Record<string, unknown>;
  if (typeof state['scriptId'] !== 'string') return false;
  if (typeof state['beatId'] !== 'string') return false;
  if (typeof state['flags'] !== 'object' || state['flags'] === null) return false;
  if (!Array.isArray(state['history'])) return false;

  return true;
}
